import { useQuery } from '@tanstack/react-query';
import { safetyApi, type SafetyRollup as SafetyRollupData } from '../../lib/apiClient';
import { StateView } from '../../components';

// Standalone-page styles (the workspace's .pw-* classes aren't in scope here).
const css = `
  .srollup-scroll { max-width: 1100px; margin: 0 auto; }
  .srollup-eyebrow {
    font: var(--ew-fw-medium) var(--ew-fs-meta)/1 var(--ew-font);
    letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-400);
  }
  .srollup-title {
    font: var(--ew-fw-semibold) 28px/1.15 var(--ew-font); letter-spacing: -0.01em;
    color: var(--ew-dark); margin: 6px 0 var(--ew-s-4);
  }
  .srollup-banner {
    display: flex; align-items: center; gap: var(--ew-s-3); background: #fff;
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md);
    padding: 10px var(--ew-s-4); margin-bottom: var(--ew-s-5);
  }
  .srollup-banner-tag {
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: .06em;
    text-transform: uppercase; color: var(--ew-quality);
  }
  .srollup-banner-note { font: var(--ew-fw-regular) 12px var(--ew-font); color: var(--ew-n-500); }
  .srollup-kpis { display: grid; gap: var(--ew-s-4); grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); margin-bottom: var(--ew-s-5); }
  .srollup-kpi {
    background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md);
    padding: var(--ew-s-4); position: relative; overflow: hidden;
    display: flex; flex-direction: column; gap: var(--ew-s-1);
  }
  .srollup-kpi-strip { position: absolute; left: 0; top: 0; bottom: 0; width: 3px; }
  .srollup-kpi-label { font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); }
  .srollup-kpi-value { font: var(--ew-fw-semibold) 22px/1.1 var(--ew-font); font-variant-numeric: tabular-nums; letter-spacing: -0.01em; color: var(--ew-dark); }
  .srollup-card { background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); }
  .srollup-card-hd {
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); margin: 0; padding: 14px var(--ew-s-4);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .srollup-table { width: 100%; border-collapse: collapse; }
  .srollup-table th {
    text-align: right; font: var(--ew-fw-medium) var(--ew-fs-meta)/1 var(--ew-font);
    letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500);
    padding: 10px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); white-space: nowrap;
  }
  .srollup-table th.srollup-th-job { text-align: left; }
  .srollup-table td {
    padding: 11px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-dark);
    text-align: right; font-variant-numeric: tabular-nums;
  }
  .srollup-table tr:last-child td { border-bottom: 0; }
  .srollup-table td.srollup-td-job { text-align: left; }
  .srollup-job { font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1.2 var(--ew-font); color: var(--ew-dark); }
  .srollup-jobnum { font: var(--ew-fw-regular) var(--ew-fs-meta)/1.2 var(--ew-font); color: var(--ew-n-500); margin-top: 2px; }
`;

const TILES: { key: keyof SafetyRollupData['totals']; label: string; strip: string }[] = [
  { key: 'goodCatch',           label: 'Good Catches',            strip: 'var(--ew-green)' },
  { key: 'siteInspection',      label: 'Site Safety Inspections', strip: 'var(--ew-quality)' },
  { key: 'equipmentInspection', label: 'Equipment Inspections',   strip: 'var(--ew-performance)' },
];

export default function SafetyRollup() {
  const query = useQuery<SafetyRollupData>({
    queryKey: ['safety-rollup'],
    queryFn: () => safetyApi.rollup(),
    staleTime: 60_000,
  });

  const banner = (
    <div className="srollup-banner">
      <span className="srollup-banner-tag">◇ Synced from E&amp;W Safety</span>
      <span className="srollup-banner-note">read-only mirror</span>
    </div>
  );

  return (
    <>
      <style>{css}</style>
      <div className="srollup-scroll">
        <div className="srollup-eyebrow">Company-wide</div>
        <h1 className="srollup-title">Safety Rollup</h1>
        {banner}

        <StateView
          query={query}
          isEmpty={(d) => !d?.available}
          empty="Not available — couldn't reach E&W Safety (check db_datareader on safety-site)."
        >
          {(data) => (
            <>
              <div className="srollup-kpis">
                {TILES.map((t) => (
                  <div key={t.key} className="srollup-kpi">
                    <span className="srollup-kpi-strip" style={{ background: t.strip }} />
                    <span className="srollup-kpi-label">{t.label}</span>
                    <span className="srollup-kpi-value">{data.totals[t.key]}</span>
                  </div>
                ))}
              </div>

              <div className="srollup-card">
                <h2 className="srollup-card-hd">By job</h2>
                {data.byJob.length === 0 ? (
                  <p style={{ padding: 'var(--ew-s-4)', color: 'var(--ew-n-400)', textAlign: 'center', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
                    No safety submissions recorded yet.
                  </p>
                ) : (
                  <table className="srollup-table">
                    <thead>
                      <tr>
                        <th className="srollup-th-job">Job</th>
                        <th>Good Catches</th>
                        <th>Site Inspections</th>
                        <th>Equipment Inspections</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byJob.map((r) => (
                        <tr key={r.jobNumber}>
                          <td className="srollup-td-job">
                            <div className="srollup-job">{r.title}</div>
                            <div className="srollup-jobnum">{r.jobNumber}</div>
                          </td>
                          <td>{r.goodCatch}</td>
                          <td>{r.siteInspection}</td>
                          <td>{r.equipmentInspection}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </StateView>
      </div>
    </>
  );
}
